import EditIcon from "@material-ui/icons/Edit";
import moment from "moment-timezone";
import { useEffect, useRef, useState } from "react";
import { AutocompleteInput, Button, Create, Datagrid, DateInput, Filter, List, Loading, ReferenceInput, SimpleForm, TextField, TextInput, TopToolbar, required, useDataProvider, useListContext, useNotify } from "react-admin";
import { useForm } from "react-final-form";
import { Link, useLocation } from "react-router-dom";
import { EpgListingEditToolbar, EpgListingForm, getEpgListingErrorMessage, redirectToEpgEditListing } from "./epg-listing-form";

const AutoCompleteChannel = (props) => {
  const dataProvider = useDataProvider();
  const form = useForm();

  const fetchChannel = async (networkCode) => {
    if (!networkCode) {
      form.change("network_code", undefined);
      return;
    }

    const result = await dataProvider.getManyChannelsAutocompleteByNetworkCode({
      ids: [networkCode],
    });

    form.change("network_code", result.data[0]?.network_code);
  };

  return (
    <ReferenceInput
      {...props}
      label="Search Channel Name"
      source="channel_autocomplete"
      reference="channels-autocomplete-by-network-code"
      onChange={(val) => {
        fetchChannel(val);
      }}
      filterToQuery={(searchText) => {
        if (searchText) {
          return { name_tc: searchText };
        }
        return {};
      }}
    >
      <AutocompleteInput optionText="channel_text" optionValue="network_code" fullWidth />
    </ReferenceInput>
  );
};

const PreviewDateInput = (props) => {
  const form = useForm();
  const previousValue = useRef(form.getState().values.ref_date || moment().format("YYYY-MM-DD"));

  useEffect(() => form.subscribe(({ values }) => {
    if (values.ref_date) {
      previousValue.current = values.ref_date;
    }
  }, { values: true }), [form]);

  return <DateInput {...props} parse={(value) => value || previousValue.current} />;
};

const EpgListingFilter = (props) => (
  <Filter {...props}>
    <PreviewDateInput label="Preview Date" source="ref_date" validate={required()} alwaysOn />
    <AutoCompleteChannel alwaysOn />
    <TextInput label="Content ID" source="network_code" alwaysOn disabled />
  </Filter>
);

const ListActions = () => (
  <TopToolbar>
    <Button component={Link} to="/epg-edit-listing" label="EPG Edit List">
      <EditIcon />
    </Button>
  </TopToolbar>
);

const EpgListingEditButton = ({ record }) => {
  const { filterValues = {} } = useListContext();
  const query = new URLSearchParams();

  query.set("epg_id", record.epg_item_id);
  if (filterValues.network_code) {
    query.set("network_code", filterValues.network_code);
  }
  if (filterValues.ref_date) {
    query.set("ref_date", moment(filterValues.ref_date).format("YYYY-MM-DD"));
  }

  return (
    <Button component={Link} to={`/epg-listing/create?${query.toString()}`} label="Edit">
      <EditIcon />
    </Button>
  );
};

export const EpgListingList = (props) => {
  return (
    <List {...props} actions={<ListActions />} bulkActionButtons={false} filters={<EpgListingFilter />} filterDefaultValues={{ ref_date: moment().format("YYYY-MM-DD"), network_code: "J" }} pagination={false}>
      <Datagrid>
        <TextField label="Start Time" source="event_datetime" />
        <TextField label="Programme ID" source="programme_id" />
        <TextField label="Programme Name" source="tc_clean_title" />
        <TextField label="Episode No." source="episode_no" />
        <EpgListingEditButton />
      </Datagrid>
    </List>
  );
};

export const EpgListingCreate = (props) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const location = useLocation();
  const [initialValues, setInitialValues] = useState(null);
  const onFailure = (error) => {
    notify(getEpgListingErrorMessage(error), "warning");
  };

  useEffect(() => {
    const fetchEpgItem = async () => {
      const urlParams = new URLSearchParams(location.search);
      const epgId = urlParams.get("epg_id");
      const refDate = urlParams.get("ref_date");
      const networkCode = urlParams.get("network_code");

      if (!epgId) {
        setInitialValues({});
        return;
      }

      try {
        const result = await dataProvider.getOneEpgListing({
          id: epgId,
          network_code: networkCode,
          ref_date: refDate,
        });
        setInitialValues({
          ...result.data,
          epg_item_id: result.data.epg_item_id || epgId,
          epg_network_code: result.data.epg_network_code || networkCode,
          epg_ref_date: result.data.epg_ref_date || refDate,
        });
      } catch (error) {
        notify(getEpgListingErrorMessage(error), "warning");
        setInitialValues({});
      }
    };

    fetchEpgItem();
  }, [dataProvider, location.search, notify]);

  if (!initialValues) {
    return <Loading />;
  }

  return (
    <Create {...props} onFailure={onFailure} mutationMode="pessimistic">
      <SimpleForm initialValues={initialValues} toolbar={<EpgListingEditToolbar />} redirect={redirectToEpgEditListing}>
        <EpgListingForm />
      </SimpleForm>
    </Create>
  );
};
